import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Tabs,
  Tab,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  Collapse,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const CapitalOverview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [filtersOpen, setFiltersOpen] = useState(!isMobile); // Collapsed on mobile by default
  
  const [capital, setCapital] = useState({ totalCapital: 0, remainingCapital: 0 });
  const [overview, setOverview] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [incomeDetails, setIncomeDetails] = useState([]);
  const [loadingIncome, setLoadingIncome] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [netProfit, setNetProfit] = useState(0);
  const [loadingProfit, setLoadingProfit] = useState(false);
  const [selectedArea, setSelectedArea] = useState(''); // Area filter
  const [areas, setAreas] = useState([]);
  const [totalOverview, setTotalOverview] = useState(null);
  const [showTotalOverview, setShowTotalOverview] = useState(false);

  useEffect(() => {
    fetchAreas();
    fetchCapital();
    fetchOverview();
    fetchNetProfit();
    if (showTotalOverview) {
      fetchTotalOverview();
    }
    
    // Auto-refresh every 10 seconds for real-time updates
    const interval = setInterval(() => {
      fetchCapital();
      fetchOverview();
      fetchNetProfit();
      if (showTotalOverview) {
        fetchTotalOverview();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [startDate, endDate, selectedArea, showTotalOverview]);
  
  const fetchAreas = async () => {
    try {
      const response = await api.get('/admin/areas');
      setAreas(response.data || []);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };
  
  const fetchTotalOverview = async () => {
    try {
      const response = await api.get('/admin/capital/total-overview');
      setTotalOverview(response.data);
    } catch (error) {
      console.error('Error fetching total overview:', error);
    }
  };

  const fetchCapital = async () => {
    try {
      const params = {};
      if (selectedArea) {
        params.areaId = selectedArea;
      }
      const response = await api.get('/admin/capital/current', { params });
      setCapital(response.data);
    } catch (error) {
      console.error('Error fetching capital:', error);
    }
  };

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const params = {
        startDate,
        endDate,
      };
      if (selectedArea) {
        params.areaId = selectedArea;
      }
      const response = await api.get('/admin/capital/overview', { params });
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = () => {
    fetchOverview();
    fetchNetProfit();
  };

  const fetchNetProfit = async () => {
    setLoadingProfit(true);
    try {
      const params = {
        startDate,
        endDate,
      };
      if (selectedArea) {
        params.areaId = selectedArea;
      }
      const response = await api.get('/admin/capital/profit', { params });
      setNetProfit(response.data.totalProfit || 0);
    } catch (error) {
      console.error('Error fetching net profit:', error);
      setNetProfit(0);
    } finally {
      setLoadingProfit(false);
    }
  };

  const handleIncomeClick = async () => {
    setIncomeDialogOpen(true);
    setLoadingIncome(true);
    setTabValue(0);
    try {
      const params = {
        startDate,
        endDate,
      };
      if (selectedArea) {
        params.areaId = selectedArea;
      }
      const response = await api.get('/admin/capital/income-details', { params });
      setIncomeDetails(response.data.incomeDetails || []);
    } catch (error) {
      console.error('Error fetching income details:', error);
      setIncomeDetails([]);
    } finally {
      setLoadingIncome(false);
    }
  };

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return `Rs ${num.toFixed(2)}`;
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    return date.toLocaleString();
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  // Group income by date
  const incomeByDate = incomeDetails.reduce((acc, item) => {
    const date = item.transactionDate ? new Date(item.transactionDate).toLocaleDateString() : 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  // Group income by field officer
  const incomeByCollector = incomeDetails.reduce((acc, item) => {
    const collector = item.collector || 'Unknown';
    if (!acc[collector]) {
      acc[collector] = { total: 0, items: [] };
    }
    acc[collector].total += Number(item.amount || 0);
    acc[collector].items.push(item);
    return acc;
  }, {});

  const totalIncome = incomeDetails.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
            <Typography 
          variant="h4"
          sx={{ 
            fontWeight: 700,
            color: '#0D47A1',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            mb: { xs: 2, sm: 3 },
            textShadow: '0 2px 4px rgba(13, 71, 161, 0.2)',
          }}
        >
          Capital Overview
        </Typography>
        
        {/* Mobile Filter Toggle */}
        {isMobile && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setFiltersOpen(!filtersOpen)}
            sx={{ mb: 2 }}
          >
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>
        )}
        
        {/* Filters Section */}
        <Collapse in={filtersOpen}>
          <Paper 
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(13, 71, 161, 0.15)',
              border: '1px solid #0D47A1',
              bgcolor: '#FFFFFF',
            }}
          >
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Filter by Area"
                    value={selectedArea}
                    onChange={(e) => setSelectedArea(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="">All Areas</MenuItem>
                    {areas.map((area) => (
                      <MenuItem key={area.id} value={area.id}>
                        {area.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="From Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="To Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} sx={{ height: '100%' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleDateRangeChange}
                      startIcon={<CalendarTodayIcon />}
                      sx={{ minHeight: '40px' }}
                    >
                      Apply
                    </Button>
                    <Button
                      fullWidth
                      variant={showTotalOverview ? "contained" : "outlined"}
                      onClick={() => setShowTotalOverview(!showTotalOverview)}
                      startIcon={<AssessmentIcon />}
                      color="primary"
                      sx={{ minHeight: '40px', display: { xs: 'none', sm: 'flex' } }}
                    >
                      {showTotalOverview ? 'Hide' : 'Show'} Total
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
              {isMobile && (
                <Button
                  fullWidth
                  variant={showTotalOverview ? "contained" : "outlined"}
                  onClick={() => setShowTotalOverview(!showTotalOverview)}
                  startIcon={<AssessmentIcon />}
                  color="primary"
                >
                  {showTotalOverview ? 'Hide' : 'Show'} Total Overview
                </Button>
              )}
            </Stack>
          </Paper>
        </Collapse>
      </Box>

      {/* Total Capital Overview */}
      {showTotalOverview && totalOverview && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: '#0D47A1', color: '#FFFFFF', boxShadow: '0 4px 20px rgba(13, 71, 161, 0.4)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#FFFFFF' }}>
            Total Capital Overview (All Areas)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatCurrency(totalOverview.totalCapital || 0)}
              </Typography>
              <Typography variant="body2">Total Capital</Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" sx={{ mb: 2 }}>Area Breakdown</Typography>
              <Grid container spacing={2}>
                {totalOverview.areaBreakdown?.map((area) => (
                  <Grid item xs={12} sm={6} md={4} key={area.areaId}>
                    <Card variant="outlined" sx={{ bgcolor: '#FFFFFF', color: '#0D47A1', border: '2px solid #0D47A1' }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                          {area.areaName}
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#0D47A1', fontWeight: 700 }}>
                          {formatCurrency(area.capital || 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#424242' }}>
                          Income: {formatCurrency(area.totalIncome || 0)} | 
                          Expenses: {formatCurrency(area.totalExpenses || 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Key Metrics Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: '#0D47A1', 
            color: '#FFFFFF', 
            height: '100%',
            transition: 'transform 0.2s',
            boxShadow: '0 4px 12px rgba(13, 71, 161, 0.3)',
            '&:hover': { 
              transform: 'scale(1.02)',
              boxShadow: '0 6px 16px rgba(13, 71, 161, 0.4)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <AccountBalanceIcon sx={{ mr: 1, fontSize: { xs: 32, sm: 40 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Total Capital</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
                {formatCurrency(capital.totalCapital)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Overall company capital
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: 'success.main', 
            color: 'success.contrastText', 
            height: '100%',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <AccountBalanceIcon sx={{ mr: 1, fontSize: { xs: 32, sm: 40 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Remaining Capital
                  {selectedArea && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, display: 'block', opacity: 0.8, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      ({areas.find(a => a.id === parseInt(selectedArea))?.name || 'Selected Area'})
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
                {formatCurrency(overview?.remainingCapital || capital.remainingCapital)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {selectedArea ? 'Available capital for selected area' : 'Available capital'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'info.main', 
              color: 'info.contrastText', 
              height: '100%',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { 
                transform: 'scale(1.02)',
                boxShadow: 6
              }
            }}
            onClick={handleIncomeClick}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <TrendingUpIcon sx={{ mr: 1, fontSize: { xs: 32, sm: 40 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Total Income
                  {selectedArea && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, display: 'block', opacity: 0.8, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      ({areas.find(a => a.id === parseInt(selectedArea))?.name || 'Selected Area'})
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
                {formatCurrency(overview?.totalIncome || 0)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {selectedArea ? 'Income for selected area' : 'Click to view details'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: 'warning.main', 
            color: 'warning.contrastText', 
            height: '100%',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <TrendingDownIcon sx={{ mr: 1, fontSize: { xs: 32, sm: 40 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Total Expenses</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
                {formatCurrency(overview?.totalExpenses || 0)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Period expenses
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: netProfit >= 0 ? 'success.main' : 'error.main', 
            color: 'white', 
            height: '100%',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <AttachMoneyIcon sx={{ mr: 1, fontSize: { xs: 32, sm: 40 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Net Profit
                  {selectedArea && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, display: 'block', opacity: 0.8, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      ({areas.find(a => a.id === parseInt(selectedArea))?.name || 'Selected Area'})
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
                {loadingProfit ? '...' : formatCurrency(netProfit)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {selectedArea ? 'Profit for selected area' : 'From paid installments'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Flow Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <PaymentIcon sx={{ mr: 1, color: 'error.main', fontSize: { xs: 28, sm: 32 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Outgoing Loans
                </Typography>
              </Box>
              <Typography variant="h3" color="error" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, wordBreak: 'break-word' }}>
                {formatCurrency(overview?.totalOutgoing || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Bank deposits disbursed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <AttachMoneyIcon sx={{ mr: 1, color: 'success.main', fontSize: { xs: 28, sm: 32 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Capital Additions
                </Typography>
              </Box>
              <Typography variant="h3" color="success.main" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, wordBreak: 'break-word' }}>
                {formatCurrency(overview?.totalAdditions || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Manual capital additions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <AssessmentIcon sx={{ mr: 1, color: overview?.netFlow >= 0 ? 'success.main' : 'error.main', fontSize: { xs: 28, sm: 32 } }} />
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Net Cash Flow
                </Typography>
              </Box>
              <Typography
                variant="h3"
                color={overview?.netFlow >= 0 ? 'success.main' : 'error.main'}
                sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, wordBreak: 'break-word' }}
              >
                {formatCurrency(overview?.netFlow || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Income + Additions - Outgoing - Expenses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary Table */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Financial Summary ({formatDate(startDate)} to {formatDate(endDate)})
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}><strong>Item</strong></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}><strong>Amount</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Total Capital</TableCell>
                  <TableCell align="right">
                    {formatCurrency(overview?.totalCapital || capital.totalCapital)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'success.light' }}>
                  <TableCell><strong>Total Income (Installments)</strong></TableCell>
                  <TableCell align="right" sx={{ color: 'success.dark', fontWeight: 'bold' }}>
                    +{formatCurrency(overview?.totalIncome || 0)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'success.light' }}>
                  <TableCell>Capital Additions</TableCell>
                  <TableCell align="right" sx={{ color: 'success.dark' }}>
                    +{formatCurrency(overview?.totalAdditions || 0)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'error.light' }}>
                  <TableCell>Outgoing Loans (Bank Deposits)</TableCell>
                  <TableCell align="right" sx={{ color: 'error.dark' }}>
                    -{formatCurrency(overview?.totalOutgoing || 0)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'error.light' }}>
                  <TableCell>Total Expenses</TableCell>
                  <TableCell align="right" sx={{ color: 'error.dark' }}>
                    -{formatCurrency(overview?.totalExpenses || 0)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Net Cash Flow</strong></TableCell>
                  <TableCell align="right">
                    <strong style={{ 
                      color: overview?.netFlow >= 0 ? '#2e7d32' : '#d32f2f' 
                    }}>
                      {overview?.netFlow >= 0 ? '+' : ''}{formatCurrency(overview?.netFlow || 0)}
                    </strong>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <TableCell><strong>Remaining Capital</strong></TableCell>
                  <TableCell align="right">
                    <strong>
                      {formatCurrency(overview?.remainingCapital || capital.remainingCapital)}
                    </strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Income Details Dialog */}
      <Dialog 
        open={incomeDialogOpen} 
        onClose={() => setIncomeDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Income Details ({formatDate(startDate)} to {formatDate(endDate)})
            </Typography>
            <IconButton onClick={() => setIncomeDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="All Income" />
            <Tab label="By Date" />
            <Tab label="By Field Officer" />
          </Tabs>

          {loadingIncome ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : incomeDetails.length === 0 ? (
            <Alert severity="info">No income records found for the selected date range.</Alert>
          ) : (
            <>
              {/* Summary */}
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="h6">
                  Total Income: {formatCurrency(totalIncome)}
                </Typography>
                <Typography variant="body2">
                  {incomeDetails.length} payment(s) recorded
                </Typography>
              </Alert>

              {/* All Income Tab */}
              {tabValue === 0 && (
                <TableContainer component={Paper} sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}><strong>Date & Time</strong></TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}><strong>Customer</strong></TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}><strong>NIC</strong></TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}><strong>Loan ID</strong></TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}><strong>Inst #</strong></TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}><strong>Field Officer</strong></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}><strong>Amount</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incomeDetails.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{formatDateTime(item.transactionDate)}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>{item.customerName || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>{item.customerNIC || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{item.loanId || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{item.installmentNumber || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>{item.collector || 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
                            {formatCurrency(item.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* By Date Tab */}
              {tabValue === 1 && (
                <Box>
                  {Object.keys(incomeByDate).sort().reverse().map((date) => {
                    const dateItems = incomeByDate[date];
                    const dateTotal = dateItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
                    return (
                      <Card key={date} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{date}</Typography>
                            <Chip 
                              label={`Total: ${formatCurrency(dateTotal)}`} 
                              color="success" 
                              size="small"
                            />
                          </Box>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Customer</strong></TableCell>
                                  <TableCell><strong>Loan ID</strong></TableCell>
                                  <TableCell><strong>Installment</strong></TableCell>
                                  <TableCell><strong>Field Officer</strong></TableCell>
                                  <TableCell align="right"><strong>Amount</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {dateItems.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{item.customerName || item.customerNIC || 'N/A'}</TableCell>
                                    <TableCell>{item.loanId || 'N/A'}</TableCell>
                                    <TableCell>#{item.installmentNumber || 'N/A'}</TableCell>
                                    <TableCell>{item.collector || 'N/A'}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}

              {/* By Field Officer Tab */}
              {tabValue === 2 && (
                <Box>
                  {Object.keys(incomeByCollector).sort().map((collector) => {
                    const collectorData = incomeByCollector[collector];
                    return (
                      <Card key={collector} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{collector}</Typography>
                            <Chip 
                              label={`Total: ${formatCurrency(collectorData.total)}`} 
                              color="primary" 
                              size="small"
                            />
                          </Box>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Date</strong></TableCell>
                                  <TableCell><strong>Customer</strong></TableCell>
                                  <TableCell><strong>Loan ID</strong></TableCell>
                                  <TableCell><strong>Installment</strong></TableCell>
                                  <TableCell align="right"><strong>Amount</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {collectorData.items.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{formatDate(item.transactionDate)}</TableCell>
                                    <TableCell>{item.customerName || item.customerNIC || 'N/A'}</TableCell>
                                    <TableCell>{item.loanId || 'N/A'}</TableCell>
                                    <TableCell>#{item.installmentNumber || 'N/A'}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncomeDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CapitalOverview;
